/**
 * Branding-Ableitung: EINE Markenfarbe → vollständige, kontrastsichere
 * Akzent-Skala für Light und Dark, plus Grundton-Paletten (warm/neutral/kühl).
 *
 * Die Zielwerte stammen aus der OKLCH-Vermessung der sechs handgetunten
 * v5-Akzente (docs/roadmap-v6.md §5) — mit einer bewussten Korrektur: Die
 * Vorlage war NICHT kontrastsicher (gold 3.40, green 3.02, teal 3.43 als
 * Text auf hellem Grund; AA verlangt 4.5). Deshalb wird die Helligkeit hier
 * nicht einfach übernommen, sondern so weit nachgeführt, bis der Kontrast
 * gegen die Flächen des gewählten Grundtons garantiert ist.
 */

import { contrastRatio, hexToOklch, normalizeHex, oklchToHex, withAlpha } from './color.js';

export type ThemeMode = 'light' | 'dark';

/** Grundton der Flächen: warm = heutiges Aurora-Beige, neutral = reines Grau, kühl = Blaugrau. */
export type NeutralTone = 'warm' | 'neutral' | 'cool';

export const NEUTRAL_TONES: readonly NeutralTone[] = ['warm', 'neutral', 'cool'];

export interface AccentScale {
  /** Hauptakzent — muss als Text auf bg/surface des Grundtons AA (4.5:1) erfüllen. */
  accent: string;
  /** Hover/aktive Variante: hell dunkler, dunkel heller — Kontrast steigt in beiden Fällen. */
  accentStrong: string;
  /** Getönte Fläche (8-stelliges Hex mit Alpha, wie die bisherigen color-mix-Werte). */
  accentSoft: string;
  /** Textfarbe AUF Akzentflächen (Buttons) — ebenfalls kontrastgeprüft. */
  accentContrast: string;
}

/**
 * Neutrale Flächen-/Text-Tokens eines Grundtons für einen Modus.
 * `primary*` ist das Light-Trio (Ink-Buttons); im Dark-Modus übernimmt
 * die Akzent-Skala die primary-Rolle (siehe brandTokens).
 */
export interface ToneTokens {
  bg: string;
  surface: string;
  surfaceHover: string;
  surfaceActive: string;
  border: string;
  borderStrong: string;
  text: string;
  textMuted: string;
  textFaint: string;
  primary: string;
  primaryHover: string;
  primaryContrast: string;
}

/**
 * Warm ist die vermessene Ist-Palette aus tokens.css — sie bleibt
 * byte-identisch, damit Bestandsinstanzen sich nicht um eine Nuance
 * verschieben. Neutral/kühl entstehen per Farbton-Tausch bei identischem
 * L und C je Token: alle Helligkeitsverhältnisse (und damit die
 * Kontrastanker der Akzent-Ableitung) bleiben mathematisch gleich.
 */
const WARM_LIGHT: ToneTokens = {
  bg: '#f6f5f2',
  surface: '#ffffff',
  surfaceHover: '#f1efe9',
  surfaceActive: '#eae7df',
  border: '#e4e1d8',
  borderStrong: '#c9c4b6',
  text: '#211d16',
  textMuted: '#6d675c',
  textFaint: '#9b9485',
  primary: '#211d16',
  primaryHover: '#38321f',
  primaryContrast: '#f8f5ee',
};

const WARM_DARK: ToneTokens = {
  bg: '#12100d',
  surface: '#1b1815',
  surfaceHover: '#24201b',
  surfaceActive: '#2c2721',
  border: '#2e2a23',
  borderStrong: '#453e32',
  text: '#ece7dd',
  textMuted: '#a89f90',
  textFaint: '#7a7264',
  // Dark-primary kommt aus der Akzent-Skala; hier nur der neutrale Fallback.
  primary: '#ece7dd',
  primaryHover: '#ffffff',
  primaryContrast: '#12100d',
};

/** Farbton des kühlen Grundtons (Blaugrau). */
const COOL_HUE = 250;

function retone(base: ToneTokens, transform: (color: string) => string): ToneTokens {
  const result = {} as Record<keyof ToneTokens, string>;
  for (const key of Object.keys(base) as (keyof ToneTokens)[]) {
    result[key] = transform(base[key]);
  }
  return result;
}

function swapHue(hex: string, hue: number | null): string {
  const { l, c } = hexToOklch(hex);
  if (hue === null) return oklchToHex({ l, c: 0, h: 0 });
  return oklchToHex({ l, c, h: hue });
}

const TONES: Record<NeutralTone, Record<ThemeMode, ToneTokens>> = {
  warm: { light: WARM_LIGHT, dark: WARM_DARK },
  neutral: {
    light: retone(WARM_LIGHT, (hex) => swapHue(hex, null)),
    dark: retone(WARM_DARK, (hex) => swapHue(hex, null)),
  },
  cool: {
    light: retone(WARM_LIGHT, (hex) => swapHue(hex, COOL_HUE)),
    dark: retone(WARM_DARK, (hex) => swapHue(hex, COOL_HUE)),
  },
};

export function toneTokens(tone: NeutralTone, mode: ThemeMode): ToneTokens {
  return TONES[tone][mode];
}

/** AA für normalen Text — die Garantie, die die v5-Vorlage gerissen hat. */
const MIN_CONTRAST = 4.5;

/**
 * Stellt die OKLCH-Helligkeit so ein, dass der Kontrast gegen den Anker
 * mindestens `target` beträgt. Startwert ist der ästhetische Zielwert aus
 * der Vermessung; verletzt er die Garantie, wird L per Binärsuche in
 * `direction` nachgeführt (light: dunkler, dark: heller). Die Buntheit wird
 * dabei je Schritt gamut-gemappt — der Farbton bleibt stehen.
 */
function solveLightness(
  start: { l: number; c: number; h: number },
  anchor: string,
  target: number,
  direction: 'darker' | 'lighter',
): string {
  const startHex = oklchToHex(start);
  if (contrastRatio(startHex, anchor) >= target) return startHex;

  let lo = direction === 'darker' ? 0.05 : start.l;
  let hi = direction === 'darker' ? start.l : 0.99;
  // Bei 'darker' wächst der Kontrast Richtung lo, bei 'lighter' Richtung hi.
  for (let i = 0; i < 40; i += 1) {
    const mid = (lo + hi) / 2;
    const ratio = contrastRatio(oklchToHex({ ...start, l: mid }), anchor);
    const satisfied = ratio >= target;
    if (direction === 'darker') {
      if (satisfied) lo = mid;
      else hi = mid;
    } else if (satisfied) hi = mid;
    else lo = mid;
  }
  const l = direction === 'darker' ? lo : hi;
  return oklchToHex({ ...start, l });
}

/** Dunkle Textfarbe auf Akzentflächen — der warme Ink aus tokens.css. */
const DARK_INK = '#191512';

function bestContrastOn(accent: string): string {
  return contrastRatio('#ffffff', accent) >= contrastRatio(DARK_INK, accent) ? '#ffffff' : DARK_INK;
}

/**
 * Kernfunktion des Branding-Fundaments (Etappe 1): eine Markenfarbe →
 * Akzent-Skala für einen Modus, kontrastsicher gegen den Grundton.
 *
 * Regeln aus der Vermessung (roadmap-v6 §5):
 * - light: L ≈ min(Marke, 0.63); strong = ΔL −0.08; soft = 13 % Alpha
 * - dark:  L ≈ light + 0.19 (mind. 0.75 — die Vorlage liegt bei 0.73–0.79,
 *          im Dunkelmodus dürfen Akzente leuchten), Buntheit reduziert
 *          (C' = min(C, 0.10 + 0.25·C)); strong = ΔL +0.05; soft = 16 % Alpha
 * - beide: L wird nachgeführt, bis Akzent-als-Text AA gegen surface-active
 *          erfüllt. Das ist bewusst NICHT bg: in dieser Palette liegt
 *          weiß > bg > hover > aktiv — die strengste Fläche, auf der
 *          Akzent-Text real vorkommt (selektierte Zeile), ist surface-active.
 */
export function deriveAccent(
  brandHex: string,
  mode: ThemeMode,
  tone: NeutralTone = 'warm',
): AccentScale {
  const brand = hexToOklch(normalizeHex(brandHex));
  const tones = toneTokens(tone, mode);

  if (mode === 'light') {
    const accent = solveLightness(
      { l: Math.min(brand.l, 0.63), c: brand.c, h: brand.h },
      tones.surfaceActive,
      MIN_CONTRAST,
      'darker',
    );
    const accentL = hexToOklch(accent).l;
    // Fast-schwarze Akzente haben kein „dunkler" — dann hellt der
    // Hover-Zustand auf (die einzige sichtbare Richtung).
    const strongL = accentL - 0.08 >= 0.05 ? accentL - 0.08 : accentL + 0.08;
    return {
      accent,
      accentStrong: oklchToHex({ l: strongL, c: brand.c, h: brand.h }),
      accentSoft: withAlpha(accent, 0.13),
      accentContrast: bestContrastOn(accent),
    };
  }

  // Dark leitet sich vom finalen Light-Akzent ab (so wurden die v5-Paare
  // vermessen) — dadurch wirken beide Modi als Paar derselben Marke.
  const lightAccentL = hexToOklch(deriveAccent(brandHex, 'light', tone).accent).l;
  const darkChroma = Math.min(brand.c, 0.1 + 0.25 * brand.c);
  const accent = solveLightness(
    { l: Math.max(lightAccentL + 0.19, 0.75), c: darkChroma, h: brand.h },
    tones.surfaceActive,
    MIN_CONTRAST,
    'lighter',
  );
  const accentL = hexToOklch(accent).l;
  return {
    accent,
    accentStrong: oklchToHex({ l: Math.min(0.99, accentL + 0.05), c: darkChroma, h: brand.h }),
    accentSoft: withAlpha(accent, 0.16),
    accentContrast: bestContrastOn(accent),
  };
}

/**
 * Komplettes CSS-Variablen-Set einer Marke für beide Modi — das, was die
 * Shell zur Laufzeit setzt und der Baukasten (Etappe 3) live in die
 * Vorschau spiegelt. Im Dark-Modus übernimmt der Akzent die primary-Rolle
 * (wie heute Gold), im Light-Modus bleibt primary der Ink des Grundtons.
 */
export function brandTokens(
  brandHex: string,
  tone: NeutralTone,
): Record<ThemeMode, Record<string, string>> {
  const result = {} as Record<ThemeMode, Record<string, string>>;
  for (const mode of ['light', 'dark'] as const) {
    const tones = toneTokens(tone, mode);
    const scale = deriveAccent(brandHex, mode, tone);
    const primary =
      mode === 'light'
        ? {
            '--au-primary': tones.primary,
            '--au-primary-hover': tones.primaryHover,
            '--au-primary-contrast': tones.primaryContrast,
          }
        : {
            '--au-primary': scale.accent,
            '--au-primary-hover': scale.accentStrong,
            '--au-primary-contrast': scale.accentContrast,
          };
    result[mode] = {
      '--au-bg': tones.bg,
      '--au-surface': tones.surface,
      '--au-surface-hover': tones.surfaceHover,
      '--au-surface-active': tones.surfaceActive,
      '--au-border': tones.border,
      '--au-border-strong': tones.borderStrong,
      '--au-text': tones.text,
      '--au-text-muted': tones.textMuted,
      '--au-text-faint': tones.textFaint,
      '--au-accent': scale.accent,
      '--au-accent-strong': scale.accentStrong,
      '--au-accent-soft': scale.accentSoft,
      '--au-accent-contrast': scale.accentContrast,
      '--au-focus': scale.accent,
      ...primary,
    };
  }
  return result;
}
